import { useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Check, ChevronRight, Undo2 } from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import {
  LineKindBadge,
  LineKindMarker,
  POLineItemProductCell,
} from '@/components/po-line-item-product-cell'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { SupplierRecipientEnvelopeEditor } from '@/components/supplier-recipient-envelope-editor'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { POAccessorialsControl } from '@/features/purchase-orders/accessorials'
import { accessorialSummary, readAccessorials } from '@/features/purchase-orders/accessorials-model'
import { POPdfView } from '@/features/purchase-orders/pdf-view'
import { formatMoney } from '@/lib/utils'
import { formatRelativeTime } from '../ui/format'
import {
  apiErrors,
  poReviewQueryKey,
  useProjectPoReview,
  useUpdateHeldPurchaseOrderRecipients,
  type HeldPurchaseOrder,
  type PurchaseOrderItem,
} from './api'

// Sales's PO review inside the project's own PO rows (USA-1333): each held PO opens to what it
// says and who gets it, and is marked reviewed; marking the last one sends them all.

const VIEWS = [
  { value: 'details', label: 'Details' },
  { value: 'pdf', label: 'PDF' },
] as const

const KIND_LABELS: Partial<Record<PurchaseOrderItem['line_kind'], string>> = {
  configuration: 'Option',
  accessory: 'Accessory',
  custom: 'Custom',
  warranty: 'Warranty',
}

function money(value: string | number | null | undefined) {
  return value == null || value === '' ? '—' : formatMoney(value)
}

function isChild(item: PurchaseOrderItem) {
  return item.parent_quote_line_id != null && item.line_kind !== 'main'
}

/**
 * A sub-line's left gutter: its (W) kind marker, then the chevron. The gutter has a fixed width
 * so the chevron sits at the same indent whether or not the line has a marker.
 */
function SubLineGutter({ item, className }: { item: PurchaseOrderItem; className?: string }) {
  return (
    <span
      className={`flex w-8 shrink-0 items-center justify-end gap-1 ${className ?? ''}`}
      data-sub-line-gutter="true"
    >
      <LineKindMarker kind={item.line_kind} />
      <ChevronRight aria-hidden="true" className="size-3 shrink-0 text-muted-foreground" />
    </span>
  )
}

function Product({ item, compactImage }: { item: PurchaseOrderItem; compactImage?: boolean }) {
  const charge = item.line_kind === 'fee' || item.line_kind === 'freight'
  return (
    <div className="min-w-0">
      {charge ? (
        <p className="text-sm font-medium">{item.description || item.model}</p>
      ) : (
        <POLineItemProductCell
          collapseLongDescription
          collapsedDescriptionLines={1}
          description={item.description}
          imageClassName={compactImage ? 'h-9 w-9' : undefined}
          imageUrl={item.image_url}
          model={item.model}
          productUuid={item.product_uuid}
          showManufacturer={false}
        />
      )}
      {isChild(item) && KIND_LABELS[item.line_kind] ? (
        <LineKindBadge className="mt-1" kind={item.line_kind} label={KIND_LABELS[item.line_kind]} />
      ) : null}
    </div>
  )
}

/** The PO's lines as every PO view shows them: quantity, list, discount, Net and total. */
function Lines({ items, total }: { items: PurchaseOrderItem[]; total: string }) {
  return (
    <div className="@container/held-po-lines">
      <div className="hidden overflow-x-auto @2xl/held-po-lines:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead className="w-px text-right">Qty</TableHead>
              <TableHead className="w-px text-right">List</TableHead>
              <TableHead className="w-px text-right">Discount</TableHead>
              <TableHead className="w-px text-right">Net</TableHead>
              <TableHead className="w-px text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item, index) => {
              const child = isChild(item)
              return (
                <TableRow className={child ? 'bg-muted/25' : undefined} key={`${item.display_number}-${index}`}>
                  <TableCell className="min-w-0 whitespace-normal">
                    <div className={child ? 'flex min-w-0 items-start gap-2' : 'min-w-0'}>
                      {child ? <SubLineGutter className="h-5" item={item} /> : null}
                      <Product item={item} />
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{Number(item.quantity)}</TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                    {money(item.list_price)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                    {item.discount_chain?.trim() || '—'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                    {money(item.unit_cost)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm font-medium tabular-nums">
                    {money(item.extended_cost)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
      <div className="divide-y @2xl/held-po-lines:hidden">
        {items.map((item, index) => {
          const child = isChild(item)
          return (
            <div
              className={`flex min-w-0 items-start gap-3 px-3 py-3 ${child ? 'ml-5 bg-muted/25' : ''}`}
              key={`${item.display_number}-${index}`}
            >
              {child ? <SubLineGutter className="mt-2 h-5" item={item} /> : null}
              <div className="min-w-0 flex-1">
                <Product compactImage item={item} />
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-semibold tabular-nums">{money(item.extended_cost)}</p>
                <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                  {Number(item.quantity)} × {money(item.unit_cost)}
                </p>
                {item.discount_chain?.trim() && item.list_price != null ? (
                  <>
                    <p className="text-xs text-muted-foreground tabular-nums">List {money(item.list_price)}</p>
                    <p className="text-xs text-muted-foreground tabular-nums">{item.discount_chain} off</p>
                  </>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
      <div className="flex items-center justify-end gap-6 border-t px-4 py-2.5 text-sm">
        <span className="text-muted-foreground">PO total</span>
        <span className="font-semibold tabular-nums">{money(total)}</span>
      </div>
    </div>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 p-4">
      <h4 className="text-sm font-semibold">{label}</h4>
      <div className="mt-2 text-sm break-words">{children}</div>
    </div>
  )
}

/** Whether this PO is reviewed, by whom, and the control that changes it. */
function ReviewControl({
  order,
  onMark,
  pending,
}: {
  order: HeldPurchaseOrder
  onMark: (reviewed: boolean) => void
  pending: boolean
}) {
  if (order.reviewed_at) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-400">
          <Check aria-hidden="true" className="size-4" />
          Reviewed
          <span className="font-normal text-muted-foreground">
            {[order.reviewed_by?.name, formatRelativeTime(order.reviewed_at)].filter(Boolean).join(' · ')}
          </span>
        </span>
        <LoadingButton loading={pending} loadingText="Undoing..." onClick={() => onMark(false)} size="sm" variant="ghost">
          <Undo2 aria-hidden="true" />
          Undo
        </LoadingButton>
      </div>
    )
  }
  return (
    <LoadingButton
      disabled={order.readiness_errors.length > 0}
      loading={pending}
      loadingText="Saving..."
      onClick={() => onMark(true)}
      size="sm"
    >
      <Check aria-hidden="true" />
      Mark reviewed
    </LoadingButton>
  )
}

/**
 * One held PO, opened in its project row: who it goes to, its ship-to and delivery, its lines (or
 * its PDF), and whether Sales has reviewed it.
 */
export function HeldPurchaseOrderDetails({
  projectUuid,
  purchaseOrderUuid,
  onMark,
  marking,
}: {
  projectUuid: string
  purchaseOrderUuid: string
  onMark: (uuids: string[], reviewed: boolean) => void
  marking: boolean
}) {
  const client = useQueryClient()
  const { data: review, isPending } = useProjectPoReview(projectUuid)
  const recipients = useUpdateHeldPurchaseOrderRecipients(projectUuid)
  const [view, setView] = useState<(typeof VIEWS)[number]['value']>('details')
  const order = review?.purchase_orders.find((item) => item.uuid === purchaseOrderUuid)
  if (!order) {
    return isPending ? <p className="px-4 py-3 text-sm text-muted-foreground">Loading the PO</p> : null
  }
  const draft = order.draft
  const ship = draft.ship_to
  const street = [ship.address1, ship.address2].filter(Boolean).join(', ')
  const locality = [ship.city, [ship.state, ship.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
  const contact = [ship.contact_name, ship.phone].filter(Boolean).join(' · ')
  const freight = draft.freight
  const instructions = [draft.shipping_instructions, draft.po_instructions].filter(Boolean).join('\n')
  const editable = order.status === 'po_held' && review?.state === 'reviewing'
  const delivery = accessorialSummary(readAccessorials(order.accessorial_snapshot))

  return (
    <div className="@container/held-po" data-held-po={order.uuid}>
      {order.readiness_errors.length ? (
        <ul
          className="space-y-1 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
          role="alert"
        >
          {order.readiness_errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-2.5">
        {editable ? (
          <ReviewControl onMark={(reviewed) => onMark([order.uuid], reviewed)} order={order} pending={marking} />
        ) : (
          <span />
        )}
        <ReviewChoiceGroup label={`${draft.po_number} view`} onChange={setView} options={VIEWS} value={view} />
      </div>
      {view === 'pdf' ? (
        <div className="p-4">
          <POPdfView poNumber={draft.po_number} poUuid={order.uuid} />
        </div>
      ) : (
        <>
          <div className="grid border-b @2xl/held-po:grid-cols-3 @2xl/held-po:divide-x">
            <div className="min-w-0 border-b @2xl/held-po:border-b-0">
              <SupplierRecipientEnvelopeEditor
                disabled={!editable}
                envelope={{ ...draft.recipient, source: draft.recipient.source ?? 'missing' }}
                id={`held-po-recipient-${order.uuid}`}
                onSave={(envelope, callbacks) =>
                  recipients.mutate(
                    {
                      purchaseOrderUuid: order.uuid,
                      recipient: { email: envelope.email ?? '', name: envelope.name, cc_emails: envelope.cc_emails },
                    },
                    {
                      onSuccess: callbacks.onSuccess,
                      onError: (error) =>
                        callbacks.onError(new Error(apiErrors(error, 'Recipients were not saved. Try again.')[0])),
                    },
                  )
                }
                pending={recipients.isPending}
                title="Sends to"
              />
            </div>
            <div className="border-b @2xl/held-po:border-b-0">
              <Fact label="Ship to">
                <span className="block">{ship.name ?? ship.company}</span>
                {street ? <span className="block">{street}</span> : null}
                {locality ? <span className="block">{locality}</span> : null}
                {contact ? <span className="block text-muted-foreground">{contact}</span> : null}
              </Fact>
            </div>
            <Fact label="Delivery">
              <span className="block">{freight.we_book_freight ? 'USA-RS books freight' : 'Manufacturer books freight'}</span>
              {freight.carrier && freight.we_book_freight ? (
                <span className="block text-muted-foreground">
                  {[freight.carrier, freight.service].filter(Boolean).join(' · ')}
                </span>
              ) : null}
              {editable ? (
                <dl className="mt-2 grid grid-cols-1 items-start">
                  <POAccessorialsControl
                    onSaved={() => void client.invalidateQueries({ queryKey: poReviewQueryKey(projectUuid) })}
                    poUuid={order.uuid}
                    snapshot={order.accessorial_snapshot}
                  />
                </dl>
              ) : (
                <span className="block text-muted-foreground">{delivery}</span>
              )}
            </Fact>
          </div>
          <Lines items={draft.items} total={draft.total} />
          {instructions ? (
            <div className="border-t px-4 py-3">
              <h4 className="text-xs font-medium text-muted-foreground">Supplier instructions</h4>
              <p className="mt-1 text-sm whitespace-pre-line">{instructions}</p>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
